import { useCallback, useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabase";
import { api, ApiError, debugMode, lastTrace, type Me, type Property } from "./lib/api";
import { AppHeader } from "./components/AppHeader";
import { DemoContext, readDemo, writeDemo } from "./lib/demo";
import { DebugPanel } from "./components/DebugPanel";
import { Login } from "./routes/Login";
import { Setup } from "./routes/Setup";
import { Explore } from "./routes/Explore";
import { Guides } from "./routes/Guides";
import { Funnel } from "./routes/Funnel";
import { Dashboard } from "./routes/Dashboard";

const LAST_KEY = "analytics:lastProperty";

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Property | null>(null);
  const [adding, setAdding] = useState(false);
  const [bootError, setBootError] = useState<string | null>(null);
  const [bootFailure, setBootFailure] = useState<unknown>(null);
  const [loadingProps, setLoadingProps] = useState(true);
  const [view, setView] = useState<"explore" | "funnel" | "analyses" | "guides">("explore");
  const [demo, setDemoState] = useState(readDemo);
  const setDemo = (v: boolean) => { writeDemo(v); setDemoState(v); };

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) return;
    api.bootstrap()
      .then((r) => setWorkspaceId(r.workspaces[0]?.id ?? null))
      .catch((e) => {
        const err = e as ApiError;
        setBootError(err.hint ?? `Could not load your workspace (${err.status}).`);
        setBootFailure(err.body);
      });
    api.me().then(setMe).catch(() => {});
  }, [session]);

  const reloadProperties = useCallback(async () => {
    if (!workspaceId) return;
    setLoadingProps(true);
    try {
      const { properties: list } = await api.listProperties(workspaceId);
      setProperties(list);
      setActiveId((cur) => {
        if (cur && list.some((p) => p.id === cur)) return cur;
        const remembered = localStorage.getItem(LAST_KEY);
        if (remembered && list.some((p) => p.id === remembered)) return remembered;
        return list[0]?.id ?? null;
      });
    } catch (e) {
      const err = e as ApiError;
      setBootError(err.hint ?? `Could not load your clients (${err.status}).`);
      setBootFailure(err.body);
    } finally {
      setLoadingProps(false);
    }
  }, [workspaceId]);

  useEffect(() => { void reloadProperties(); }, [reloadProperties]);

  useEffect(() => {
    if (activeId) localStorage.setItem(LAST_KEY, activeId);
  }, [activeId]);

  // Several clients open in several tabs is the normal way to use this.
  useEffect(() => {
    const name = properties.find((p) => p.id === activeId)?.name;
    document.title = name ? `${name} · Analytics` : "Analytics";
  }, [activeId, properties]);

  if (!ready) return <Shell><p className="muted">Loading.</p></Shell>;
  if (!session) return <Login />;

  const params = new URLSearchParams(window.location.search);
  const connectionId = params.get("connection");
  const connId = connectionId
    ?? me?.connections.find((c) => c.status === "active")?.id
    ?? null;

  if (!workspaceId) {
    return (
      <Shell>
        {bootError ? <div className="warning">{bootError}</div>
                   : <p className="muted">Setting up your workspace.</p>}
        {(debugMode() || bootFailure !== null) && (
          <DebugPanel trace={lastTrace} error={bootFailure ?? undefined} />
        )}
        <button className="quiet" onClick={() => supabase.auth.signOut()}>Sign out</button>
      </Shell>
    );
  }

  const active = properties.find((p) => p.id === activeId) ?? null;
  const needsSetup = adding || editing !== null
    || Boolean(connectionId)
    || (!loadingProps && properties.length === 0);

  const header = (
    <AppHeader
      demo={demo}
      onToggleDemo={() => setDemo(!demo)}
      me={me}
      properties={properties}
      activeId={activeId}
      onSelect={(id) => { setActiveId(id); setAdding(false); setEditing(null); }}
      onAdd={() => { setAdding(true); setEditing(null); }}
      onEdit={(p) => { setEditing(p); setAdding(false); }}
      onDelete={async (p) => {
        if (!confirm(`Remove ${p.name}? This deletes the client and its saved sources. Your BigQuery data is untouched.`)) return;
        await api.deleteProperty(p.id).catch(() => {});
        if (activeId === p.id) setActiveId(null);
        await reloadProperties();
      }}
    />
  );

  const wrap = (children: React.ReactNode) => (
    <DemoContext.Provider value={{ demo, setDemo }}>
      {demo && (
        <div className="demo-bar">
          <strong>Sample data.</strong> Nothing here is from your property. Numbers
          are generated to show what each tool does.
          <button onClick={() => setDemo(false)}>Switch to real data</button>
        </div>
      )}
      {children}
    </DemoContext.Provider>
  );

  if (needsSetup) {
    return wrap(
      <>
        {header}
        <Setup
          connectionId={connId}
          workspaceId={workspaceId}
          editing={editing}
          onCancel={properties.length > 0
            ? () => { setAdding(false); setEditing(null); stripConnectionParam(); }
            : undefined}
          onOpenGuide={() => { setAdding(false); setEditing(null); setView("guides"); }}
          onSaved={async (id) => {
            setAdding(false); setEditing(null);
            stripConnectionParam();
            await reloadProperties();
            setActiveId(id);
          }}
        />
      </>
    );
  }

  if (loadingProps) return wrap(<Shell><p className="muted">Loading clients.</p></Shell>);

  return wrap(
    <>
      {header}
      {active ? (
        <>
          <nav className="viewnav">
            <div className="viewnav-in">
              <button className={view === "explore" ? "on" : ""}
                      onClick={() => setView("explore")}>Explore</button>
              <button className={view === "funnel" ? "on" : ""}
                      onClick={() => setView("funnel")}>Funnel</button>
              <button className={view === "analyses" ? "on" : ""}
                      onClick={() => setView("analyses")}>Analyses</button>
              <button className={view === "guides" ? "on" : ""}
                      onClick={() => setView("guides")}>Setup guides</button>
            </div>
          </nav>
          {view === "guides" ? <Guides />
            : view === "funnel"
            ? <Funnel property={active} connectionId={connId}
                      onChangeSources={() => setEditing(active)} />
            : view === "explore"
            ? <Explore property={active} connectionId={connId}
                       onChangeSources={() => setEditing(active)} />
            : <Dashboard property={active} connectionId={connId}
                         onChangeSources={() => setEditing(active)} />}
        </>
      ) : (
        <Shell><p className="muted">Pick a client from the menu above.</p></Shell>
      )}
    </>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="shell">{children}</div>;
}

/** The OAuth callback leaves ?connection= behind; drop it once consumed. */
function stripConnectionParam() {
  const u = new URL(window.location.href);
  if (u.searchParams.has("connection")) {
    u.searchParams.delete("connection");
    window.history.replaceState({}, "", u.toString());
  }
}
